import { DOCUMENT } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BundledialogComponent } from "../bundledialog/bundledialog.component";
import { Location } from "@angular/common";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { FlowQueryModel } from "src/app/models/flowquerymodel";
import { ToastrService } from "ngx-toastr";
import { AccessRoleInfoComponent } from "../access-role-info/access-role-info.component";

@Component({
  selector: "app-blogs",
  templateUrl: "./blogs.component.html",
  styleUrls: ["./blogs.component.scss"],
})
export class BlogsComponent implements OnInit {
  blogs: any = [];
  noResultReturned: boolean;
  loadMore: boolean;
  pageSize: any;
  isLoading!: boolean;
  /** Master list — never mutated after load. */
  contents: any = [];
  /** Displayed list — derived from contents by applyFilter(). */
  filteredBlogs: any = [];
  add_circle_outlinebgColor: string;
  add_circle_outlineSize: string;
  userData: any;
  add_circle_outlineColor: string;
  selectedFilter: string = "all";
  noBlogFoundMessage: string;
  permissionList: any = [];
  canCreateBlog: boolean = true;
  canViewBlogs: any;
  input: string = "";

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private _cdf: ChangeDetectorRef,
    private flowService: FlowServiceService,
    public location: Location,
    private eventEmitterService: EventEmitterService,
    private toastr: ToastrService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.pageSize = 12;
    this.noResultReturned = false;
    this.loadMore = false;
  }

  ngOnInit(): void {
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
    this.isLoading = true;
    this.getBlogs();
    this.permissionList = JSON.parse(
      localStorage.getItem("PremissionToActions") || "{}"
    );

    this.canCreateBlog = !!this.permissionList["Blog"]?.["Create/Edit"];
    this.canViewBlogs = !!this.permissionList["Blog"]?.["View"];

    this.eventEmitterService.setUserProfileCreateIconClickedEvent(
      "emoji_objects"
    );
    localStorage.setItem("selectedBlogContent", null);
  }

  // ── Search ────────────────────────────────────────────────────────────────

  /** Called by the input's (input) event — instantly filter in memory. */
  searchedBlogs() {
    this.applyFilter();
  }

  /** Clear the search bar and show all blogs. */
  clearBlogSearch() {
    this.input = "";
    this.selectedFilter = "all";
    this.applyFilter();
  }

  /**
   * Client-side filter applied to this.contents:
   *  • empty input  → show everything
   *  • "@xyz"       → blogs where userName OR authorName contains "xyz"
   *  • "xyz"        → blogs where title OR authorName OR userName contains "xyz"
   */
  applyFilter() {
    const raw = (this.input || "").trim();

    if (!raw) {
      this.filteredBlogs = [...this.contents];
      this.noBlogFoundMessage =
        this.contents.length === 0 ? "No blogs found." : "";
      return;
    }

    let results: any[];

    if (raw.startsWith("@")) {
      const term = raw.slice(1).toLowerCase();
      results = this.contents.filter(
        (blog) =>
          (blog.userName || "").toLowerCase().includes(term) ||
          (blog.authorName || "").toLowerCase().includes(term)
      );
    } else {
      const term = raw.toLowerCase();
      results = this.contents.filter(
        (blog) =>
          (blog.title || "").toLowerCase().includes(term) ||
          (blog.authorName || "").toLowerCase().includes(term) ||
          (blog.userName || "").toLowerCase().includes(term)
      );
    }

    this.filteredBlogs = results;
    this.noBlogFoundMessage = results.length === 0 ? "No blogs found." : "";
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  async getBlogs() {
    this.noResultReturned = true;
    this.isLoading = true;

    // Capture & immediately clear navigation-context keys so they don't
    // persist across page refreshes.
    const emailAddress = localStorage.getItem("SelectedUserEmail");
    const selectedUserName = localStorage.getItem("SelectedUserName");
    localStorage.removeItem("SelectedUserEmail");
    localStorage.removeItem("SelectedUserName");

    // Pre-fill search bar when arriving from a user's profile / blog detail.
    if (emailAddress && selectedUserName) {
      const cleanName = (selectedUserName || "").replace(/^@+/, "");
      this.input = cleanName ? `@${cleanName}` : "";
    }

    (await this.flowService.getAllBlogs()).subscribe((data) => {
      this.contents = (data || [])
        .filter(
          (blog) =>
            new Date(blog.timestamp).toISOString() !==
            "0001-01-01T00:00:00.000Z"
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

      this.noResultReturned = false;
      this.isLoading = false;

      // Apply any pre-filled filter (e.g. @username from navigation).
      this.applyFilter();
    });
  }

  // ── Dropdown filter (All / My Blogs) ─────────────────────────────────────

  filterBlogs() {
    if (!this.userData) {
      localStorage.setItem("returnUrl", this.router.url);
      this.router.navigate(["/registration"]);
      return;
    }

    if (this.selectedFilter === "myBlogs") {
      const myEmail = (
        this.userData.emailAddress ||
        this.userData.partitionKey ||
        ""
      ).toLowerCase();
      this.filteredBlogs = this.contents.filter(
        (blog) => (blog.partitionKey || "").toLowerCase() === myEmail
      );
      this.noBlogFoundMessage =
        this.filteredBlogs.length === 0
          ? "You haven't written any blogs yet. Start your first one now! 😊"
          : "";
    } else {
      // "all" — re-run the text filter so any typed query is respected.
      this.applyFilter();
    }
  }

  // ── Navigation helpers ────────────────────────────────────────────────────

  loadMoreTips() {}
  onScrollDown() {}
  setLoadMore(tips: any) {
    this.loadMore = !(tips.length < this.pageSize.Value);
  }
  onTabChanged(event: any) {}
  onRightClick(event: any) {}

  getTitle(title: any) {
    return title.replace(/\s+/g, "-");
  }

  viewblog(blog: any) {
    localStorage.setItem("selectedBlogContent", JSON.stringify(blog));
    this.router.navigate(["/blog/" + this.getTitle(blog.title)]);
  }

  openDialog() {
    if (this.userData != null) {
      this.eventEmitterService.setUserProfileCreateIconClickedEvent(
        "add_circle_outline"
      );
      this.add_circle_outlinebgColor = "white";
      this.add_circle_outlineColor = "#5ea1d8 !important";
      this.add_circle_outlineSize = "25px !important";
      localStorage.removeItem("ItemsRequired");
      localStorage.removeItem("flowCoverPageDetails");
      localStorage.removeItem("stepsCoverPageDetails");
      localStorage.removeItem("draftWord");
      localStorage.removeItem("editWord");
      const dialogRef = this.dialog.open(BundledialogComponent, {
        panelClass: "dialoguebox",
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === "bundle") {
          localStorage.setItem(
            "navigatebackfromcreatebundle",
            this.location.path()
          );
          localStorage.removeItem("createbundledetails");
          localStorage.removeItem("addbundleselectedflows");
          this.router.navigate(["/create-guide-pack"]);
        } else if (result === "flow") {
          localStorage.setItem(
            "navigatebackfromcreateflow",
            this.location.path()
          );
          this.router.navigate(["/create-guide"]);
        } else {
          this.eventEmitterService.setUserProfileCreateIconClickedEvent(
            "emoji_objects"
          );
          window.location.reload();
        }
      });
    } else {
      this.router.navigate(["/login"]);
    }
  }

  goToBlog() {
    if (this.userData != null && !this.canCreateBlog) {
      this.toastr.info(
        "Currently you do not have access to create flow/bundle please request access to admin"
      );
      this.dialog.open(AccessRoleInfoComponent, {
        width: "400px",
        disableClose: true,
      });
    } else if (this.userData != null) {
      localStorage.setItem("selectedBlogContent", null);
      this.router.navigate(["/blog"]);
    } else {
      localStorage.setItem("returnUrl", this.router.url);
      this.router.navigate(["/registration"]);
    }
  }
}
